'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons/faArrowLeft';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight';
import { Button, Icon } from '@benzinga/core-ui';
import { TickerCard, TickerCardQuote } from '../TickerCard';
import { CryptoQuote, useTickerData } from '../Ticker/useTickerData';
import { convertToTickerCardQuote } from '../Ticker/utils';
import { DelayedQuote } from '@benzinga/quotes-manager';

export interface TickerCardCarouselProps {
  tickers: string[];
}

export const TickerCardCarousel: React.FC<TickerCardCarouselProps> = ({ tickers }) => {
  const [currentTicker, setCurrentTicker] = React.useState(tickers?.[0]);
  if (!Array.isArray(tickers)) return null;

  const currentIndex = tickers.findIndex(ticker => ticker === currentTicker);

  const handleChangePrev = () => {
    if (!Array.isArray(tickers) || currentIndex === 0) return;
    const prevItemIndex = tickers[currentIndex - 1];
    if (prevItemIndex) {
      setCurrentTicker(prevItemIndex);
    }
  };

  const handleChangeNext = () => {
    if (!Array.isArray(tickers) || currentIndex === tickers.length - 1) return;
    const nextItem = tickers[currentIndex + 1];
    if (nextItem) {
      setCurrentTicker(nextItem);
    }
  };

  return (
    <Container className="ticker-card-carousel">
      <TickerCardCarouselItem ticker={currentTicker} />
      <div className="next-prev-buttons-container">
        <Button className="prev-button" onClick={handleChangePrev}>
          <Icon icon={faArrowLeft} />
        </Button>
        <Button className="buy-stock-button" variant="flat-blue">
          BUY STOCK
        </Button>
        <Button className="next-button" onClick={handleChangeNext}>
          <Icon icon={faArrowRight} />
        </Button>
      </div>
    </Container>
  );
};

const TickerCardCarouselItem: React.FC<{ ticker: string }> = ({ ticker }) => {
  const { cryptoQuote, stockQuote } = useTickerData(ticker);
  const tickerCardQuote = convertToTickerCardQuote({
    cryptoQuote: cryptoQuote as CryptoQuote,
    stockQuote: stockQuote as DelayedQuote,
  }) as TickerCardQuote;
  return <TickerCard quote={tickerCardQuote} />;
};

const Container = styled.div`
  &.ticker-card-carousel {
    display: flex;
    flex-direction: column;
    max-width: 320px;

    .next-prev-buttons-container {
      display: flex;
      height: 40px;
      margin-top: 0.5rem;

      > button {
        height: 100%;
      }

      .prev-button,
      .next-button {
        width: 50px;
      }

      .buy-stock-button {
        width: 100%;
        margin: 0 0.5rem;
        font-size: ${({ theme }) => theme.fontSize.lg};
      }
    }
  }
`;
